import { useMemo } from 'react';

import type { ClassroomDashboardRes } from '@/api/gen/_model/classroomDashboardRes';
import {
  useGetJoinedClassrooms,
  useGetOwnedClassrooms,
} from '@/api/gen/classroom/classroom.gen';
import { useViewport } from '@/hooks';
import type { ClassroomListSortMode } from '@/pages/classrooms/components/ClassroomListSearchToolbar';
import { ClassroomListTab } from '@/pages/classrooms/components/ClassroomListTabs';
import type { ClassroomItem } from '@/pages/classrooms/shared/components/ClassroomList/ClassroomCards';
import { CLASSROOM_ROUTES } from '@/shared/consts/routes';
import { toClassroomBackgroundRef } from '@/shared/features/classroom/utils/classroomAvatarColor';
import { selectData } from '@/shared/utils';

function toClassroomItem(
  classroom: ClassroomDashboardRes,
  isClassroomOwner: boolean,
  useOwnerMobileEntry: boolean,
): ClassroomItem {
  return {
    id: classroom.classGroupId,
    name: classroom.name,
    subtitle: classroom.ownerName,
    ownerAvatarData: classroom.ownerAvatarData,
    memberCount: classroom.memberCount ?? 0,
    isOwner: isClassroomOwner,
    background: toClassroomBackgroundRef(classroom.background),
    href: CLASSROOM_ROUTES.detail(classroom.classGroupId, isClassroomOwner, {
      isMobile: useOwnerMobileEntry,
    }),
  };
}

function sortClassroomItems(
  items: ClassroomItem[],
  sortMode: ClassroomListSortMode,
): ClassroomItem[] {
  if (sortMode === 'name') {
    return [...items].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }
  return items;
}

export function useClassroomItems({
  activeTab,
  searchText,
  sortMode,
}: {
  activeTab: ClassroomListTab;
  searchText: string;
  sortMode: ClassroomListSortMode;
}) {
  const { isMobile } = useViewport();
  const ownedQuery = useGetOwnedClassrooms(undefined, {
    query: {
      select: selectData,
      enabled: activeTab === ClassroomListTab.OWNED_CLASSROOMS,
      staleTime: 30_000,
      // 서버가 join 한 ownerAvatarData 를 진입마다 최신으로 받아 아바타 변경을 반영한다.
      refetchOnMount: 'always',
    },
  });
  const joinedQuery = useGetJoinedClassrooms(undefined, {
    query: {
      select: selectData,
      enabled: activeTab === ClassroomListTab.JOINED_CLASSROOMS,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  });

  const ownedItems = useMemo<ClassroomItem[]>(
    () =>
      (ownedQuery.data ?? []).map(classroom =>
        toClassroomItem(classroom, true, isMobile),
      ),
    [isMobile, ownedQuery.data],
  );
  const joinedItems = useMemo<ClassroomItem[]>(
    () =>
      (joinedQuery.data ?? []).map(classroom =>
        toClassroomItem(classroom, false, isMobile),
      ),
    [isMobile, joinedQuery.data],
  );

  const normalizedSearchText = searchText.trim().toLowerCase();

  const ownedClassrooms = useMemo(() => {
    const filtered = normalizedSearchText
      ? ownedItems.filter(item =>
          item.name.toLowerCase().includes(normalizedSearchText),
        )
      : ownedItems;
    return sortClassroomItems(filtered, sortMode);
  }, [ownedItems, normalizedSearchText, sortMode]);
  const joinedClassrooms = useMemo(() => {
    const filtered = normalizedSearchText
      ? joinedItems.filter(item =>
          item.name.toLowerCase().includes(normalizedSearchText),
        )
      : joinedItems;
    return sortClassroomItems(filtered, sortMode);
  }, [joinedItems, normalizedSearchText, sortMode]);

  const activeQuery =
    activeTab === ClassroomListTab.OWNED_CLASSROOMS ? ownedQuery : joinedQuery;

  return {
    ownedClassrooms,
    joinedClassrooms,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
  };
}
